import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import { allowedToView } from "../../utils/PermissionUtils.js";
import Notification from "../../components/Notification";

function MilkPrices() {
    const [milkPrices, setMilkPrices] = useState([]);
    const [price, setPrice] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [editMilkPrice, setEditMilkPrice] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    const fetchMilkPrices = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/milk-prices`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            if (response.ok) {
                setMilkPrices(await response.json());
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch milk prices");
            }
        } catch (error) {
            setNotification({ type: "danger", message: "Failed to fetch milk prices." });
        }
    }, [token, navigate]);

    useEffect(() => { fetchMilkPrices(); }, [fetchMilkPrices]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        if (!price || !startDate) {
            setErrors({ price: !price ? ["Price is required"] : [], start_date: !startDate ? ["Start date is required"] : [] });
            return;
        }

        const requestData = { price, start_date: startDate, end_date: endDate || null };
        const url = editMilkPrice ? `${API_URL}/milk-prices/${editMilkPrice.id}/update` : `${API_URL}/milk-prices`;
        const method = "POST";

        try {
            const response = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(requestData)
            });
            if (response.ok) {
                fetchMilkPrices();
                setPrice("");
                setStartDate("");
                setEndDate("");
                setEditMilkPrice(null);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setErrors(errorData || {});
                setNotification({ message: errorData.message || "An error occurred.", type: "danger" });
            }

        } catch (error) {
            setNotification({ type: "danger", message: "Failed to submit milk price." });
        }
    };

    const handleEdit = (milkPrice) => {
        setPrice(milkPrice.price);
        setStartDate(milkPrice.start_date);
        setEndDate(milkPrice.end_date || "");
        setEditMilkPrice(milkPrice);
    };

    const handleDelete = async (id) => {
        try {
            await fetch(`${API_URL}/milk-prices/${id}`, {
                method: "DELETE",
                headers: { "Authorization": `Bearer ${token}` }
            });
            fetchMilkPrices();
            setNotification({ type: "success", message: "Milk price deleted." });
        } catch (error) {
            setNotification({ type: "danger", message: "Failed to delete milk price." });
        }
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Milk Prices</p>
                    </div>
                </div>
            </nav>

            <div className="columns">
                <div className="column is-half dash-section vertical-separator">
                    <h1 className="section-title">Milk Prices</h1>
                    <table className="table is-striped is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Price</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            {milkPrices.map((milkPrice, index) => (
                                <tr key={milkPrice.id}>
                                    <td>{index + 1}</td>
                                    <td>{milkPrice.price}</td>
                                    <td>{milkPrice.start_date}</td>
                                    <td>{milkPrice.end_date || "Ongoing"}</td>
                                    <td>
                                        {canEdit(userRole) && (
                                            <>
                                                <button className="button is-white" onClick={() => handleEdit(milkPrice)}>
                                                    <span className="icon"><i className="fas fa-edit"></i></span>
                                                </button>
                                                <button className="button is-white" onClick={() => handleDelete(milkPrice.id)}>
                                                    <span className="icon"><i className="fas fa-trash-alt"></i></span>
                                                </button>
                                            </>
                                        )
                                        }

                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {canEdit(userRole) && (
                    <div className="column is-half dash-section">
                        <h1 className="section-title">{editMilkPrice ? "Edit Milk Price" : "Add New Milk Price"}</h1>
                        {notification.message && <Notification type={notification.type} message={notification.message} onClose={() => setNotification({ type: "", message: "" })} />}
                        <form onSubmit={handleSubmit}>
                            <div className="field">
                                <label className="label">Price</label>
                                <input className={`input ${errors.price ? "is-danger" : ""}`} type="number" value={price} onChange={(e) => setPrice(e.target.value)} required />
                            </div>
                            <div className="field">
                                <label className="label">Start Date</label>
                                <input className={`input ${errors.start_date ? "is-danger" : ""}`} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
                            </div>
                            <div className="field">
                                <label className="label">End Date (Optional)</label>
                                <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>
                            <button type="submit" className="button primary-bg">{editMilkPrice ? "Update" : "Add New"}</button>
                        </form>
                    </div>
                )}

            </div>
        </main>
    );
}

export default MilkPrices;
